import { useEffect, useState, Fragment } from "react";
import { api } from "./api";
import { Plus, Building2, Trash2, Save, X, Sparkles, Package, TrendingUp, ChevronRight } from "lucide-react";

const AdminMerchants = () => {
  const [merchants, setMerchants] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const refresh = async () => {
    const r = await api.listMerchants();
    setMerchants(r.data.merchants || []);
  };
  useEffect(() => { refresh(); }, []);

  const remove = async (m) => {
    if (!window.confirm(`Remove merchant "${m.company}"?`)) return;
    await api.deleteMerchant(m.id);
    refresh();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold">Merchants</h1>
          <p className="text-white/60 mt-1 text-sm">Active fulfillment clients. Won leads auto-promote here.</p>
        </div>
        <button data-testid="admin-new-merchant" onClick={() => setShowCreate(true)} className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--tt-green)] hover:bg-[var(--tt-green-dark)] px-4 py-2 text-sm font-semibold transition">
          <Plus className="h-4 w-4" /> New merchant
        </button>
      </div>

      {merchants.length === 0 ? (
        <div className="rounded-2xl bg-[#0f1512] border border-white/10 p-12 text-center">
          <Building2 className="h-10 w-10 text-white/30 mx-auto" />
          <div className="mt-4 font-display text-lg text-white/80">No merchants yet</div>
          <p className="text-sm text-white/50 max-w-sm mx-auto mt-2">
            When a lead is marked "Won" they'll auto-promote here. Or click <b>New merchant</b> to add one directly.
          </p>
        </div>
      ) : (
        <div className="rounded-2xl bg-[#0f1512] border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-xs uppercase tracking-wider text-white/50">
              <tr>
                <th className="text-left px-4 py-3">Company</th>
                <th className="text-right px-4 py-3 hidden md:table-cell">Volume</th>
                <th className="text-right px-4 py-3 hidden md:table-cell">Revenue / mo</th>
                <th className="text-right px-4 py-3">SLA (on-time)</th>
                <th className="text-right px-4 py-3 hidden md:table-cell">Shipments</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3"> </th>
              </tr>
            </thead>
            <tbody>
              {merchants.map((m) => {
                const isOpen = expanded === m.id;
                return (
                  <Fragment key={m.id}>
                    <tr className="border-t border-white/5 hover:bg-white/5">
                      <td className="px-4 py-3">
                        <button data-testid={`merchant-row-${m.id}`} onClick={() => setExpanded(isOpen ? null : m.id)} className="flex items-center gap-2 text-left">
                          <ChevronRight className={`h-4 w-4 text-white/40 transition ${isOpen ? "rotate-90" : ""}`} />
                          <span>
                            <span className="font-semibold hover:text-[var(--tt-zam-orange)] transition">{m.company}</span>
                            <div className="text-xs text-white/50">{m.contact_name}{m.marketplaces && ` · ${m.marketplaces}`}</div>
                          </span>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right hidden md:table-cell">{(m.monthly_volume || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right hidden md:table-cell">${(m.monthly_revenue || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right">
                        {m.sla && m.sla.on_time_pct !== null ? (
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                            m.sla.on_time_pct >= 99 ? "bg-green-500/10 text-green-400" : m.sla.on_time_pct >= 95 ? "bg-orange-500/10 text-orange-400" : "bg-red-500/10 text-red-400"
                          }`}>{m.sla.on_time_pct.toFixed(1)}%</span>
                        ) : (
                          <span className="text-xs text-white/40">No data</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right hidden md:table-cell">{m.sla?.total || 0}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${
                          m.status === "active" ? "bg-green-500/10 text-green-400" :
                          m.status === "onboarding" ? "bg-orange-500/10 text-orange-400" :
                          m.status === "paused" ? "bg-yellow-500/10 text-yellow-400" :
                          "bg-red-500/10 text-red-400"
                        }`}>{m.status}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => setEditing(m)} className="text-white/50 hover:text-white transition text-xs">Edit</button>
                          <span className="text-white/20">·</span>
                          <button onClick={() => remove(m)} className="text-white/40 hover:text-red-400 transition"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-t border-white/5 bg-black/20">
                        <td colSpan={7} className="px-6 py-5">
                          <MerchantDetail merchant={m} onRefreshed={refresh} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(showCreate || editing) && (
        <MerchantModal
          initial={editing}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={() => { setShowCreate(false); setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
};

const MerchantDetail = ({ merchant, onRefreshed }) => {
  const [ships, setShips] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const loadShips = async () => {
    const r = await api.merchantShipments(merchant.id);
    setShips(r.data.shipments || []);
  };
  useEffect(() => { loadShips(); /* eslint-disable-next-line */ }, [merchant.id]);

  const sla = merchant.sla || {};

  const seedDemo = async () => {
    setSeeding(true);
    try {
      await api.seedDemoShipments(merchant.id);
      await loadShips();
      onRefreshed();
    } finally { setSeeding(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SlaTile label="On-time" value={sla.on_time_pct !== null && sla.on_time_pct !== undefined ? `${sla.on_time_pct.toFixed(1)}%` : "—"} target=">99%" hue={sla.on_time_pct >= 99 ? "green" : sla.on_time_pct >= 95 ? "orange" : "red"} />
        <SlaTile label="Cancellations" value={sla.cancellation_rate !== undefined ? `${sla.cancellation_rate}%` : "—"} target="<0.5%" hue={sla.cancellation_rate < 0.5 ? "green" : sla.cancellation_rate < 2 ? "orange" : "red"} />
        <SlaTile label="Valid tracking" value={sla.valid_tracking_rate !== undefined ? `${sla.valid_tracking_rate}%` : "—"} target=">95%" hue={sla.valid_tracking_rate >= 95 ? "green" : "orange"} />
        <SlaTile label="Total shipments" value={(sla.total ?? 0).toLocaleString()} target={`${sla.last_30d ?? 0} last 30d`} />
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-white/50 flex items-center gap-2">
          <Package className="h-3.5 w-3.5" />
          Shipments feed — in production, your WMS POSTs to <code className="text-white/70">/api/admin/merchants/{merchant.id}/shipments</code>.
        </div>
        <button data-testid={`seed-demo-${merchant.id}`} onClick={seedDemo} disabled={seeding} className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--tt-zam-orange)]/15 border border-[var(--tt-zam-orange)]/30 hover:bg-[var(--tt-zam-orange)]/25 text-[var(--tt-zam-orange)] px-3 py-1.5 text-xs font-semibold transition disabled:opacity-50">
          <Sparkles className="h-3.5 w-3.5" /> {seeding ? "Seeding…" : "Seed 60 demo shipments"}
        </button>
      </div>

      {ships && ships.length > 0 && (
        <div className="rounded-lg border border-white/10 overflow-hidden">
          <div className="text-xs uppercase tracking-wider text-white/50 px-3 py-2 bg-white/5 flex items-center gap-2">
            <TrendingUp className="h-3.5 w-3.5" /> Recent shipments (showing {Math.min(ships.length, 10)})
          </div>
          <table className="w-full text-xs">
            <thead className="text-white/40">
              <tr>
                <th className="text-left px-3 py-2">Order</th>
                <th className="text-left px-3 py-2">Marketplace</th>
                <th className="text-left px-3 py-2">Carrier</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">Tracking</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">Ship time vs SLA</th>
              </tr>
            </thead>
            <tbody>
              {ships.slice(0, 10).map((s) => {
                const shipped = s.shipped_at ? new Date(s.shipped_at) : null;
                const promised = s.promised_by ? new Date(s.promised_by) : null;
                let ontime = null;
                if (shipped && promised) ontime = shipped <= promised;
                return (
                  <tr key={s.id} className="border-t border-white/5">
                    <td className="px-3 py-2 font-mono text-white/80">{s.order_id}</td>
                    <td className="px-3 py-2 text-white/70">{s.marketplace || "—"}</td>
                    <td className="px-3 py-2 text-white/70">{s.carrier || "—"}</td>
                    <td className="px-3 py-2 text-white/50 font-mono hidden md:table-cell">{s.tracking_number || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${
                        s.status === "delivered" ? "bg-green-500/10 text-green-400" :
                        s.status === "shipped" ? "bg-sky-500/10 text-sky-400" :
                        s.status === "cancelled" ? "bg-red-500/10 text-red-400" :
                        s.status === "returned" ? "bg-yellow-500/10 text-yellow-400" :
                        "bg-white/5 text-white/50"
                      }`}>{s.status}</span>
                    </td>
                    <td className="px-3 py-2 hidden md:table-cell">
                      {ontime === null ? <span className="text-white/40">—</span> : (
                        <span className={ontime ? "text-green-400" : "text-red-400"}>{ontime ? "On time ✓" : "Late ✗"}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {ships && ships.length === 0 && (
        <div className="rounded-lg bg-white/5 border border-white/10 p-4 text-sm text-white/60 text-center">
          No shipments yet. Click <b>Seed 60 demo shipments</b> to see the SLA dashboard populate.
        </div>
      )}
    </div>
  );
};

const SlaTile = ({ label, value, target, hue = "white" }) => {
  const c = hue === "green" ? "text-green-400" : hue === "orange" ? "text-[var(--tt-zam-orange)]" : hue === "red" ? "text-red-400" : "text-white";
  return (
    <div className="rounded-lg bg-white/5 border border-white/10 p-3">
      <div className="text-[10px] uppercase tracking-wider text-white/50">{label}</div>
      <div className={`text-2xl font-display font-bold mt-1 ${c}`}>{value}</div>
      <div className="text-[10px] text-white/40 mt-0.5">{target}</div>
    </div>
  );
};

const MerchantModal = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial || {
    company: "", contact_name: "", email: "", phone: "",
    marketplaces: "", monthly_volume: 0, monthly_revenue: 0,
    sla_on_time_pct: 100, status: "onboarding", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.company.trim()) return;
    setSaving(true);
    try {
      const payload = {
        company: form.company, contact_name: form.contact_name || "", email: form.email || "", phone: form.phone || "",
        marketplaces: form.marketplaces || "", monthly_volume: Number(form.monthly_volume) || 0,
        monthly_revenue: Number(form.monthly_revenue) || 0, sla_on_time_pct: Number(form.sla_on_time_pct) || 0,
        status: form.status, notes: form.notes || "",
      };
      if (initial?.id) await api.updateMerchant(initial.id, payload);
      else await api.createMerchant(payload);
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="w-full max-w-2xl bg-[#0f1512] border border-white/10 rounded-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl font-bold">{initial ? "Edit merchant" : "New merchant"}</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white transition"><X className="h-5 w-5" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            ["company", "Company *"], ["contact_name", "Contact"], ["email", "Email"], ["phone", "Phone"],
            ["marketplaces", "Marketplaces"],
          ].map(([k, l]) => (
            <label className="block" key={k}>
              <span className="text-[11px] uppercase tracking-wider text-white/50">{l}</span>
              <input value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm" />
            </label>
          ))}
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-white/50">Status</span>
            <select value={form.status} onChange={(e) => set("status", e.target.value)} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm">
              <option value="onboarding">Onboarding</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="churned">Churned</option>
            </select>
          </label>
          {[
            ["monthly_volume", "Monthly volume (orders)"],
            ["monthly_revenue", "Monthly revenue ($)"],
            ["sla_on_time_pct", "SLA on-time % (manual override)"],
          ].map(([k, l]) => (
            <label className="block" key={k}>
              <span className="text-[11px] uppercase tracking-wider text-white/50">{l}</span>
              <input type="number" value={form[k] ?? 0} onChange={(e) => set(k, e.target.value)} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm" />
            </label>
          ))}
          <label className="block sm:col-span-2">
            <span className="text-[11px] uppercase tracking-wider text-white/50">Notes</span>
            <textarea rows={3} value={form.notes ?? ""} onChange={(e) => set("notes", e.target.value)} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm resize-none" />
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 pt-4">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-white/60 hover:text-white transition">Cancel</button>
          <button onClick={save} disabled={saving || !form.company.trim()} className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--tt-green)] hover:bg-[var(--tt-green-dark)] px-4 py-2 text-sm font-semibold transition disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminMerchants;
